import { useQueries } from '@tanstack/react-query';
import { useClients } from 'src/clients/hooks/useClients';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  ClientAndAdviserNames,
  describeClientByName,
} from 'src/financial/financial-data/providerReferenceHolders';
import type { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

/** Another client recorded as an owner of an item the client owns. */
type CoOwner = ClientAndAdviserNames & { client_id: string };

// Only the generated "a client of <adviser>" and "another client" need a capital to stand alone as
// a label; a stored name such as "van Dijk" is shown as entered, as on every other screen.
const asLabel = (coOwner: CoOwner) => {
  const description = describeClientByName(coOwner);
  return coOwner.client_full_name
    ? description
    : description.charAt(0).toUpperCase() + description.slice(1);
};

/**
 * Names an item's client owners, who need not share a family or an adviser. The family roster
 * names its members; the co-owners of `clientIds` name saved owners outside it, another adviser's
 * client as "A client of <adviser>"; and the viewer's client list names a client picked as an
 * owner before the item is saved. Read only with the owners feature on, the only time owners are
 * shown.
 */
export const useOwnerNames = (
  members: FamilyMember[],
  clientIds: string[] = [],
): ((clientId: string) => string | undefined) => {
  const { flagEnabledFinancialItemOwners } = useFlags();
  const results = useQueries({
    queries: clientIds.map(clientId => ({
      queryKey: ['clients', clientId, 'co-owners'],
      enabled: !!flagEnabledFinancialItemOwners && !!clientId,
    })),
  });
  const { clients } = useClients({}, { enabled: !!flagEnabledFinancialItemOwners });

  const memberNames = new Map(
    members.map(member => [
      member.clientId,
      formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL),
    ]),
  );
  const coOwners = results.flatMap(result => (result.data as CoOwner[] | undefined) ?? []);
  const coOwnerNames = new Map(coOwners.map(coOwner => [coOwner.client_id, asLabel(coOwner)]));
  const clientNames = new Map(
    clients.map(client => [
      client.id,
      formatName(client.firstName, client.lastName, NameFormat.NATURAL),
    ]),
  );

  return clientId =>
    memberNames.get(clientId) ?? coOwnerNames.get(clientId) ?? clientNames.get(clientId);
};
